import { useCallback, useMemo, useReducer } from "react"
import axios, { CancelToken } from "axios"
import { ApiAccess } from 'features/session/types'
import { useApiAccess, useWebSockets } from "features/session/context"

type PostState<T> = {
  posting: boolean
  error: Error | null
  result: T | null
}
type PostAction<T> =
  | { type: 'clear' }
  | { type: 'start' }
  | { type: 'success'; result: T }
  | { type: 'error'; error: Error }
  | { type: 'finish' }

const postReducer = <T>(
  state: PostState<T>,
  action: PostAction<T>
): PostState<T> => {
  switch (action.type) {
    case 'clear':
      return { ...state, posting: false, error: null, result: null }
    case 'start':
      return { ...state, posting: true, error: null }
    case 'success':
      return { ...state, error: null, result: action.result }
    case 'error':
      return { ...state, error: action.error, result: null }
    case 'finish':
      return { ...state, posting: false }
  }
}

export const useApiPost = <TParams, TResult>(
  postFunc: (
    apiAccess: ApiAccess,
    cancelToken: CancelToken,
    params: TParams,
    webSocketId?: string
  ) => Promise<TResult> | null,
  resolve: (
    result: TResult,
    params: TParams,
    apiAccess: ApiAccess,
    cancelToken: CancelToken
  ) => void,
  reject: (
    reason: Error | undefined,
    params: TParams
  ) => void,
  deps?: React.DependencyList
): [PostState<TResult>, (params: TParams) => void] => {
  const socket = useWebSockets()
  const cancel = axios.CancelToken.source()
  const [state, dispatch] = useReducer(postReducer, {
    posting: false,
    error: null,
    result: null
  } as PostState<TResult>)

  const apiAccess = useApiAccess()
  const post = useCallback(
    (params: TParams) => {
      const promise = postFunc(apiAccess, cancel.token, params, socket?.id)
      if (!promise) {
        dispatch({ type: 'clear' })
        return
      }
      promise
        .then((response) => {
          dispatch({ type: 'success', result: response })
          resolve(response, params, apiAccess, cancel.token)
        })
        .catch((error) => {
          dispatch({ type: 'error', error })
          if (reject) reject(error, params)
        })
        .finally(() => {
          dispatch({ type: 'finish' })
        })
      dispatch({ type: 'start' })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    (deps ?? []).concat([postFunc, resolve, reject, dispatch/*, apiAccess, cancel*/, socket])
  )
  return useMemo(
    () => [state as PostState<TResult>, post],
    [state, post]
  )
}
